import React, { useMemo, useState } from 'react';
import {
  DSCard, DSButton, DSInput, DSBadge, DSBackLink, DSAccordion, DSAccordionItem
} from '../../design-system/primitives';
import './mergeAccounts.css';
import { personName } from '../../utils/personName';
import UserAvatar from '../../components/UserAvatar';

// Two accounts becoming one.
//
// The screen this replaces was a three-column table - source in red, target in
// green, result in blue - which read as error/success/neutral when the three
// are just roles, and which cut off the Result column at phone width. The
// column the screen exists to show was the one you could not see.
//
// Rebuilt outcome first. The order is what the admin needs to decide, not what
// the database looks like: is the money safe, what does the member end up with,
// which account survives, what carries over, what is left to do afterwards.
//
// Nothing here decides anything on its own. Every judgement the admin makes is
// passed back up; this component only shows what is true and what will happen.

const MONEY = { style: 'currency', currency: 'USD' };

// Warnings the screen presents somewhere better than a follow-up list: the
// first two are the money card's headline, the third hangs off the payment
// rows, and the last belongs to the confirm sheet.
const ALREADY_SHOWN = [
  'paid on both accounts',
  'already paid twice',
  'no amount recorded',
  'cannot be easily undone'
];

function MergeAccounts({
  preview,
  result,
  onResultChange,
  onCancel,
  onSwapDirection,
  onConfirm,
  onNotDuplicates,
  submitting = false
}) {
  const [showMonths, setShowMonths] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');

  const { source, target, money, square, warnings } = preview;

  // Fields where both accounts have something, so somebody has to choose, split
  // from fields only one side has, which carry over without a decision. A table
  // gave all four equal weight even when three had nothing in either column.
  const { decisions, carried } = useMemo(() => splitFields(source, target), [source, target]);

  const merged = money?.merged;
  const doubled = [...(money?.doubled?.source || []), ...(money?.doubled?.target || [])];
  // Follow-ups are what is LEFT after the screen has shown something. A warning
  // the money card already headlines, or that hangs off a fact above, would
  // otherwise appear twice - "7 months paid on both accounts" was rendering as
  // the money card's title and again as a follow-up, which reads as two
  // separate problems.
  const followUps = warnings.filter((w) => !ALREADY_SHOWN.some((shown) => w.message.includes(shown)));

  return (
    <div className="merge">
      <DSBackLink onClick={onCancel} className="merge__back">Reconcile</DSBackLink>

      <h2 className="merge__title">Merge two accounts</h2>
      <p className="merge__lede">
        Two accounts that look like the same person. Check the money before you commit.
      </p>

      {/* The money, first, because it is the part that cannot be checked
          anywhere else and the part a wrong merge damages permanently. */}
      {merged && merged.rowCount > 0 && (
        <MoneyCard
          money={money}
          doubled={doubled}
          expanded={showMonths}
          onToggle={() => setShowMonths((open) => !open)}
        />
      )}

      {/* What the member ends up with. The old screen's Result column, given
          the room to actually be read. */}
      <DSCard cornerVariant="lg" className="merge__result" frame="bars" headerTone="strong" density="compact"
        title="After the merge" subtitle="One account. This is what the member ends up with.">
        <div className="merge__identity">
          <UserAvatar person={target} className="merge__avatar" />
          <div>
            <strong>{displayName(target)}</strong>
            <span className="merge__identity-email">{target.email}</span>
          </div>
        </div>

        <dl className="merge__facts">
          <Fact label="Signs in with" value={target.email}>
            {source.email !== target.email && (
              <>
                {source.email} stops working as a login. It stays on file pointing here,
                so old links and Square records still resolve.
              </>
            )}
          </Fact>

          <Fact label="Membership" value={target.isMember || source.isMember ? 'Member' : 'Not a member'} />

          {merged && merged.monthCount > 0 && (
            <Fact
              label="Paid months"
              value={`${plural(merged.monthCount, 'month')}, ${monthName(merged.firstMonth)} to ${monthName(merged.lastMonth)}`}
              tone={merged.missingMonths.length > 0 ? 'warning' : undefined}
            >
              {merged.missingMonths.length > 0 && (
                <>
                  Nothing for {merged.missingMonths.map(monthName).join(', ')} on either account,
                  so they still compute as unpaid after the merge.
                </>
              )}
            </Fact>
          )}

          {merged && merged.rowCount > 0 && (
            <Fact
              label="Payment rows"
              value={`${plural(merged.rowCount, 'payment')}, ${money$(merged.total)}`}
              tone={merged.duplicatedAmount > 0 ? 'warning' : undefined}
            >
              {merged.duplicatedAmount > 0 && (
                <>
                  {money$(merged.duplicatedAmount)} of that covers{' '}
                  {plural(merged.duplicatedMonthCount, 'month')} already paid
                </>
              )}
              {merged.unknownAmounts > 0 && (
                <>
                  {merged.duplicatedAmount > 0 ? '. ' : ''}
                  {plural(merged.unknownAmounts, 'payment')} without an amount recorded,
                  so the total is lower than what was collected.
                </>
              )}
            </Fact>
          )}

          {square && square.liveSubscriptions.length > 0 && (
            <Fact
              label="Subscriptions"
              value={square.needsManualCancel
                ? `${plural(square.liveSubscriptions.length, 'live subscription')}, both move across`
                : 'One, moves across'}
              tone={square.needsManualCancel ? 'warning' : undefined}
            />
          )}

          <Fact
            label="Store credit"
            value={`${money$(Number(source.creditBalance) + Number(target.creditBalance))} across ${plural(source.creditTransactionCount + target.creditTransactionCount, 'transaction')}`}
          />
        </dl>
      </DSCard>

      {/* Which account survives. Named by what each one is rather than by
          source and target, which are words about the database. */}
      <DSCard cornerVariant="lg" frame="bars" headerTone="strong" density="compact" flush
        title="The two accounts"
        subtitle={sameName(source, target)
          ? 'Same name, two emails. This is the usual shape of a duplicate.'
          : 'Two different names. Check this is really one person.'}>
        <AccountRow account={target} months={money?.target} role="primary" />
        <AccountRow account={source} months={money?.source} role="joining" onMakePrimary={onSwapDirection} />
        <p className="merge__why-primary">
          {primaryReason(target, source)} The member signs in with this email afterwards,
          so switching changes their login.
        </p>
      </DSCard>

      {/* Fields where both accounts have a value. Where only one side has
          anything there is nothing to decide, so it collapses. */}
      {decisions.length > 0 && (
        <DSCard cornerVariant="lg" className="merge__decisions" frame="bars" headerTone="strong" density="compact"
          title={`${plural(decisions.length, 'thing')} to decide`}
          subtitle="Both accounts have something here. Pick which one to keep.">
          {decisions.map((field) => (
            <div key={field.key} className="merge__decision">
              <p className="merge__decision-label">{field.label}</p>
              {[
                { value: field.targetValue, from: 'the account that keeps the login' },
                { value: field.sourceValue, from: 'the account being merged away' }
              ].map((option) => (
                <button
                  key={option.from}
                  type="button"
                  className={`merge__option ${result[field.key] === option.value ? 'is-chosen' : ''}`}
                  onClick={() => onResultChange({ ...result, [field.key]: option.value })}
                >
                  <span className="merge__radio" aria-hidden="true" />
                  <span>
                    <strong>{option.value}</strong>
                    <span className="merge__option-from">From {option.from}</span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </DSCard>
      )}

      {carried.length > 0 && (
        <DSAccordion className="merge__carried">
          <DSAccordionItem
            title={`${plural(carried.length, 'field')} carried over automatically`}
            subtitle="Only one account had a value, so it carries over as it is. Name and email are not choices, the primary keeps its own."
          >
            <dl className="merge__facts merge__facts--plain">
              {carried.map((field) => (
                <Fact key={field.key} label={field.label} value={field.sourceValue || field.targetValue} />
              ))}
            </dl>
          </DSAccordionItem>
        </DSAccordion>
      )}

      {/* Everything the merge does not do. Square is the honest one: two live
          subscriptions both keep billing and no amount of merging here stops
          that, so it is stated as work rather than buried in a warning. */}
      {followUps.length > 0 && (
        <DSCard cornerVariant="lg" frame="bars" headerTone="strong" density="compact" flush
          title="Still to do, afterwards">
          <ul className="merge__followups">
            {followUps.map((warning, index) => (
              <li key={index} className={`merge__followup merge__followup--${warning.severity}`}>
                <span className="merge__followup-tag">{tagFor(warning)}</span>
                <span>
                  <strong>{warning.message}</strong>
                  {warning.details && <span className="merge__followup-detail">{warning.details}</span>}
                </span>
              </li>
            ))}
          </ul>
        </DSCard>
      )}

      <DSButton
        tone="primary"
        appearance="solid"
        className="merge__go"
        onClick={() => { setTyped(''); setConfirming(true); }}
      >
        Merge these accounts
      </DSButton>

      <button type="button" className="merge__not-duplicates" onClick={onNotDuplicates}>
        These are two different people
      </button>

      {/* The typed confirmation lives here rather than above, because the
          protection belongs where the irreversible thing happens. */}
      {confirming && (
        <div className="merge__sheet-overlay" onClick={(e) => e.target === e.currentTarget && setConfirming(false)}>
          <div className="merge__sheet" role="dialog" aria-modal="true" aria-label="Confirm merge">
            <h3>Merge {displayName(source)} into {target.email}?</h3>
            <p className="merge__sheet-body">
              {source.email} stops working as a login and everything on it moves across.
              This cannot be undone from the app.
            </p>
            <DSInput
              label="Type MERGE to confirm"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="MERGE"
            />
            <div className="merge__sheet-actions">
              <DSButton
                tone="danger"
                appearance="solid"
                disabled={typed.trim().toUpperCase() !== 'MERGE' || submitting}
                onClick={onConfirm}
              >
                {submitting ? 'Merging...' : 'Merge these accounts'}
              </DSButton>
              <DSButton tone="secondary" appearance="outline" onClick={() => setConfirming(false)}>
                Cancel
              </DSButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- the money -------------------------------------------------------------

// Two headlines, not one. A month paid twice on a single account is already
// true today and merging does not cause it; a month paid on BOTH accounts is
// caused by the merge and may mean these are two people who each paid. An
// admin who reads "paid twice" on a merge screen assumes the merge did it, so
// the wording has to separate them.
function MoneyCard({ money, doubled, expanded, onToggle }) {
  const shared = money.shared;
  const clean = shared.length === 0 && doubled.length === 0;

  const headline = shared.length > 0
    ? `${plural(shared.length, 'month')} paid on both accounts`
    : `${plural(doubled.length, 'month')} paid twice on the same account`;

  return (
    <DSCard
      cornerVariant="lg"
      className={`merge__money ${clean ? 'is-clean' : 'is-flagged'}`}
      frame="bars"
      headerTone="strong"
      density="compact"
      title={clean ? 'No month is paid twice' : headline}
      subtitle={clean
        ? `${plural(money.merged.monthCount, 'paid month')} across both accounts, none of them shared.`
        : shared.length > 0
          ? 'Merging keeps both payments, so the merged account will show those months covered twice. Either the club was paid twice, or these are two people who each paid.'
          : 'This is already true today and merging does not cause it. Worth settling while you are here, from the member\'s Money tab or the Payment Review Queue.'}
    >
      <div className="merge__money-body">
        {[...shared.map((m) => ({ month: m, note: 'paid on both accounts' })),
          ...doubled.map((d) => ({ month: d.month, note: `${d.count} payments on one account` }))]
          .map((row) => (
            <div key={`${row.month}-${row.note}`} className="merge__month-row">
              <strong>{monthName(row.month)}</strong>
              <span>{row.note}</span>
            </div>
          ))}

        <button type="button" className="merge__months-toggle" onClick={onToggle}>
          {expanded ? 'Hide months' : `See all ${plural(money.merged.monthCount, 'paid month')}`}
        </button>

        {expanded && (
          <div className="merge__month-strip">
            {money.merged.months.map((m) => (
              <span
                key={m.month}
                className={`merge__month ${m.count > 1 ? 'is-doubled' : ''}`}
                title={`${plural(m.count, 'payment')}, ${money$(m.amount)}`}
              >
                {monthName(m.month)}
                {m.count > 1 && <em>×{m.count}</em>}
              </span>
            ))}
            {money.merged.missingMonths.map((month) => (
              <span key={month} className="merge__month is-missing">{monthName(month)}</span>
            ))}
          </div>
        )}
      </div>
    </DSCard>
  );
}

function AccountRow({ account, months, role, onMakePrimary }) {
  return (
    <div className={`merge__account merge__account--${role}`}>
      <div className="merge__account-head">
        <span className="merge__account-role">{role === 'primary' ? 'Primary' : 'Joins it'}</span>
        {account.isMember && <DSBadge variant="success">Member</DSBadge>}
        {account.squareCustomer && <DSBadge variant="brand">Square customer</DSBadge>}
        {account.autoCreated && <DSBadge variant="neutral">Created by sync</DSBadge>}
      </div>
      <strong className="merge__account-email">{account.email}</strong>
      <p className="merge__account-meta">
        {[
          // Distinct months, not `account.paymentCount`, which counts payment
          // ROWS. They differ whenever a month was paid twice - Will Prescott is
          // 11 rows across 10 months - so labelling rows "paid months" here
          // would contradict the figures directly above it.
          plural(months ? months.monthCount : 0, 'paid month'),
          `${money$(account.creditBalance)} credit`,
          account.hasPassword ? 'has a password' : 'no password set'
        ].join(' · ')}
      </p>
      {onMakePrimary && (
        <DSButton tone="secondary" appearance="outline" size="sm" onClick={onMakePrimary}>
          Make primary
        </DSButton>
      )}
    </div>
  );
}

function Fact({ label, value, tone, children }) {
  return (
    <div className="merge__fact">
      <dt>{label}</dt>
      <dd>
        <span className={tone ? `merge__fact-value merge__fact-value--${tone}` : 'merge__fact-value'}>{value}</span>
        {children && <span className="merge__fact-note">{children}</span>}
      </dd>
    </div>
  );
}

// --- helpers ---------------------------------------------------------------

const FIELDS = [
  { key: 'sternUsername', label: 'Stern username' },
  { key: 'preferredName', label: 'Preferred name' },
  { key: 'pronouns', label: 'Pronouns' },
  { key: 'hostProfile', label: 'Host profile' }
];

// Only these four. `mergeAccounts` accepts overrides for exactly this set, so
// offering a choice on anything else would be a control the API ignores.
function splitFields(source, target) {
  const decisions = [];
  const carried = [];

  FIELDS.forEach((field) => {
    const sourceValue = source[field.key] || '';
    const targetValue = target[field.key] || '';
    if (sourceValue && targetValue && sourceValue !== targetValue) {
      decisions.push({ ...field, sourceValue, targetValue });
    } else if (sourceValue || targetValue) {
      carried.push({ ...field, sourceValue, targetValue });
    }
  });

  return { decisions, carried };
}

// An account somebody has actually signed into wins, not the one carrying more
// data. The Square sync creates accounts that accumulate every payment and
// subscription while never being used by a person, so "carries more" points at
// exactly the wrong one.
function primaryReason(target, source) {
  if (target.hasPassword && !source.hasPassword) {
    return 'Primary because this is the account they actually sign into.';
  }
  if (target.isMember && !source.isMember) {
    return 'Primary because this is the account carrying the membership.';
  }
  if (source.autoCreated && !target.autoCreated) {
    return 'Primary because the other account was created by Square sync, not by a person.';
  }
  return 'Primary because it was chosen as the account to keep.';
}

function tagFor(warning) {
  const message = warning.message.toLowerCase();
  if (message.includes('square') || message.includes('subscription')) return 'Square';
  if (message.includes('paid') || message.includes('payment') || message.includes('amount')) return 'Money';
  if (message.includes('member')) return 'Status';
  return 'Heads up';
}

function sameName(a, b) {
  return `${a.firstName} ${a.lastName}`.toLowerCase().trim()
    === `${b.firstName} ${b.lastName}`.toLowerCase().trim();
}

function displayName(account) {
  return personName(account) || account.email;
}

// Never "1 months". Every count that meets a noun goes through here.
function plural(count, noun) {
  return `${count} ${noun}${Number(count) === 1 ? '' : 's'}`;
}

function money$(value) {
  return Number(value || 0).toLocaleString('en-US', MONEY);
}

function monthName(key) {
  if (!key) return '';
  const [year, month] = key.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1))
    .toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export default MergeAccounts;
export { splitFields, primaryReason, plural, monthName, tagFor };
